/** Mirrors BPC.Vineforce.Billing.Dto (BillingAppService). */

export type BillingState = 'trial' | 'trialEnded' | 'active' | 'paymentFailed' | 'paused' | 'cancelPending' | 'ended';

export type BillingPlanName = 'monthly' | 'yearly';

export interface BillingPlan {
    plan: BillingPlanName;
    priceId: string;
    /** What Stripe charges per person per billing interval: 49 (monthly) or 468 (yearly). */
    unitAmount: number;
    /** Per person per month: 49 (monthly) or 39 (yearly). */
    monthlyEquivalent: number;
    currency: string;
}

export interface BillingStatus {
    state: BillingState;
    isBlocked: boolean;
    isBillingAdmin: boolean;
    trialDaysLeft: number | null;
    teamSize: number;
    maxPeople: number;
    plan: BillingPlanName | null;
    people: number | null;
    periodEnd: string | null;
    paymentDueBy: string | null;
    cardBrand: string | null;
    cardLast4: string | null;
    declinedOn: string | null;
    amountDue: number | null;
    ownerName: string | null;
    ownerFirstName: string | null;
    ownerProfilePictureUrl: string | null;
    plans: BillingPlan[] | null;
    yearlySavingsPerPerson: number | null;
    upgradePromptsShown: number[] | null;
    askAdminSentToday: boolean;
}

export interface PlanSelection {
    plan: BillingPlanName;
    people: number;
}

export interface CheckoutSession {
    url: string;
    sessionId: string;
}

export interface CheckoutStatus {
    confirmed: boolean;
    paid: boolean;
    plan: BillingPlanName | null;
    people: number | null;
    nextPaymentAmount: number | null;
    nextPaymentDate: string | null;
}

/** Settings → Billing → Change plan (BillingAppService.GetPlanChangeOptions). */
export interface PlanChangeOptions {
    canChange: boolean;
    cannotChangeReason: string | null;
    currentPlan: BillingPlanName | null;
    people: number;
    periodEnd: string | null;
    plans: BillingPlan[] | null;
    yearlySavingsPerPerson: number | null;
    pendingPlan: BillingPlanName | null;
    pendingPlanDate: string | null;
}

/** The older StripeController endpoints answer HTTP 200 even when they fail, with the failure in the body. */
export interface StripeHandleResult {
    isError: boolean;
    errorMessage: string | null;
}

export interface ChangePlanResult {
    outcome: 'switchedNow' | 'scheduled' | 'pendingCancelled' | 'unchanged';
    effectiveDate: string | null;
}

export interface AskAdminResult {
    sent: boolean;
    alreadySentToday: boolean;
    adminFirstName: string | null;
}

/** Story Part 3: the "Everything is included" list (same features as the website). */
export const BILLING_INCLUDED_FEATURES: readonly string[] = [
    'Emails handled',
    'Clients & cases/projects connected',
    'Tasks and follow-ups',
    'Files saved to the case',
    'Help writing replies',
    'My day for everyone',
];

export const BILLING_TALK_TO_US_HREF =
    'mailto:hello@trovve.com?subject=' + encodeURIComponent('Question about my Trovve plan');

/** "$1,404" - whole dollars without cents, otherwise two decimals. */
export function formatMoney(amount: number, currency: string): string {
    const whole = Math.round(amount * 100) % 100 === 0;
    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: (currency || 'usd').toUpperCase(),
        minimumFractionDigits: whole ? 0 : 2,
        maximumFractionDigits: whole ? 0 : 2,
    }).format(amount);
}
